import { useState, useEffect, useRef } from 'react';
import { Save, Wrench } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { NavigationBar } from '../components/ui/NavigationBar';
import { ImageUpload } from '../components/ui/ImageUpload';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { AlertModal } from '../components/ui/AlertModal';
import { useParams, useOutletContext } from 'react-router-dom';
import { Plus, Loader2 } from 'lucide-react';
import { usePreferences } from '../contexts/PreferencesContext';
import { RichTextEditor } from '../components/ui/RichTextEditor';
import { useTranslations } from '../i18n';
import { theme } from '../lib/theme';
import { LogEntryItem } from '../components/cars/LogEntryItem';
import { NoteItem } from '../components/cars/NoteItem';
import { deleteTempFiles } from '../lib/r2';
import { useQueryClient } from '@tanstack/react-query';
import { PART_KEYS, GALLERY_KEYS, REMINDER_KEYS } from '../hooks/useCarData';
import { ReminderFormSection } from '../components/cars/ReminderFormSection';
import type { ReminderFormData } from '../types/reminders';
import { ImageViewer } from '../components/ui/ImageViewer';

type SparePartModalProps = {
  onClose: () => void;
  onSaved: () => void;
  carId: string;
  sparePart?: {
    id: string;
    name: string;
    manufacturer?: string;
    part_code?: string;
    link?: string;
    price?: number;
    quantity: number;
    comment?: string;
    expense_id?: string | null;
    images?: Array<{ url: string; comment?: string }>;
    last_modified_by_email?: string | null;
  } | null;
  isReadOnly?: boolean;
};

export function SparePartModal({ onClose, onSaved, carId, sparePart, isReadOnly = false }: SparePartModalProps) {
  const { user } = useAuth();
  const { preferences } = usePreferences();
  const queryClient = useQueryClient();
  const outletContext = useOutletContext<any>() || {};
  const { effectiveUserId, ownerPreferences, selectedCar, isAdminView = false } = outletContext;
  const effectivePreferences = (ownerPreferences || preferences) ? {
    ...(ownerPreferences || preferences),
    distance_unit: selectedCar?.distance_unit || (ownerPreferences || preferences).distance_unit,
    volume_unit: selectedCar?.volume_unit || (ownerPreferences || preferences).volume_unit,
    currency: selectedCar?.currency || (ownerPreferences || preferences).currency,
    currency_symbol: selectedCar?.currency_symbol || (ownerPreferences || preferences).currency_symbol
  } : null;

  const countLimit = 100; // Standalone spare part limit

  const { t } = useTranslations();
  const { slug } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState(() => ({
    name: sparePart?.name || '',
    manufacturer: (sparePart as any)?.manufacturer || '',
    part_code: sparePart?.part_code || '',
    link: sparePart?.link || '',
    price: sparePart?.price?.toString() || '',
    quantity: sparePart?.quantity?.toString() || '1',
    comment: sparePart?.comment || ''
  }));
  const [images, setImages] = useState<Array<{ url: string; comment?: string; thumbnail?: string; sizeBytes?: number; log_entry_id?: string; note_id?: string }>>(() => {
    return sparePart?.images || [];
  });
  const [storedValues, setStoredValues] = useState<any>(() => ({
    name: sparePart?.name || '',
    manufacturer: (sparePart as any)?.manufacturer || '',
    part_code: sparePart?.part_code || '',
    link: sparePart?.link || '',
    price: sparePart?.price?.toString() || '',
    quantity: sparePart?.quantity?.toString() || '1',
    comment: sparePart?.comment || ''
  }));
  const [initialImages, setInitialImages] = useState<string>(() => {
    return JSON.stringify(sparePart?.images || []);
  });
  const [allParts, setAllParts] = useState<{ name: string; part_code: string }[]>([]);
  const [activeSuggestionField, setActiveSuggestionField] = useState<'name' | 'part_code' | null>(null);
  const suggestionRef = useRef<HTMLDivElement>(null);
  const imageUploadRef = useRef<any>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [linkedExpenses, setLinkedExpenses] = useState<any[]>([]);
  const [linkedNotes, setLinkedNotes] = useState<any[]>([]);
  const [reminderData, setReminderData] = useState<ReminderFormData | null>(null);
  const [initialReminderData, setInitialReminderData] = useState<string>('null');
  const [actualReminderId, setActualReminderId] = useState<string | null>(null);
  const [viewerImages, setViewerImages] = useState<string[]>([]);
  const [viewerIndex, setViewerIndex] = useState(0);
  const savedImageUrlsRef = useRef<Set<string>>(new Set(sparePart?.images?.map(img => img.url) || []));

  useEffect(() => {
    const fetchParts = async () => {
      try {
        const { data, error } = await supabase
          .from('spare_parts')
          .select('name, part_code');
        if (!error && data) {
          const uniqueMap = new Map();
          data.forEach(item => {
            const key = `${item.name}|${item.part_code || ''}`;
            if (!uniqueMap.has(key)) {
              uniqueMap.set(key, item);
            }
          });
          setAllParts(Array.from(uniqueMap.values()));
        }
      } catch (err) {
        console.error('Error fetching spare parts:', err);
      }
    };
    fetchParts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionRef.current && !suggestionRef.current.contains(event.target as Node)) {
        setActiveSuggestionField(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasChanges = storedValues ? (
    formData.name !== storedValues.name ||
    formData.manufacturer !== (storedValues as any).manufacturer ||
    formData.part_code !== storedValues.part_code ||
    formData.link !== storedValues.link ||
    formData.price !== storedValues.price ||
    formData.quantity !== storedValues.quantity ||
    formData.comment.trim() !== (storedValues.comment || '').trim() ||
    JSON.stringify(images) !== initialImages ||
    JSON.stringify(reminderData) !== initialReminderData
  ) : (
    formData.name.trim() !== '' ||
    images.length > 0 ||
    !!reminderData
  );

  const initialInitDone = useRef<string | null>(null);

  useEffect(() => {
    if (sparePart) {
      if (initialInitDone.current === sparePart.id) return;

      const values = {
        name: sparePart.name,
        manufacturer: (sparePart as any).manufacturer || '',
        part_code: sparePart.part_code || '',
        link: sparePart.link || '',
        price: sparePart.price?.toString() || '',
        quantity: sparePart.quantity?.toString() || '1',
        comment: sparePart.comment || ''
      };
      setFormData(values);
      setStoredValues(values);

      const partImages = sparePart.images || [];
      setImages(partImages);
      setInitialImages(JSON.stringify(partImages));

      loadPartImages(sparePart.id);
      loadLinkedRecords(sparePart.id);
      loadReminder(sparePart.id);
      initialInitDone.current = sparePart.id;
    } else {
      const emptyValues = {
        name: '',
        manufacturer: '',
        part_code: '',
        link: '',
        price: '',
        quantity: '1',
        comment: ''
      };
      setFormData(emptyValues);
      setStoredValues(emptyValues);
      setImages([]);
      setInitialImages('[]');
      setReminderData(null);
      setInitialReminderData('null');
      setActualReminderId(null);
      initialInitDone.current = null;
    }
  }, [sparePart?.id]);

  // iOS Keyboard Fix: Use Visual Viewport API
  useEffect(() => {
    if (!window.visualViewport) return;

    const vvp = window.visualViewport;
    const handleViewportChange = () => {
      if (containerRef.current) {
        containerRef.current.style.height = `${vvp.height}px`;
        containerRef.current.style.top = `${vvp.offsetTop}px`;
        if (window.scrollY !== 0) {
          window.scrollTo(0, 0);
        }
      }
    };

    vvp.addEventListener('resize', handleViewportChange);
    vvp.addEventListener('scroll', handleViewportChange);
    handleViewportChange();

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100dvh';

    return () => {
      vvp.removeEventListener('resize', handleViewportChange);
      vvp.removeEventListener('scroll', handleViewportChange);
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);

  const loadPartImages = async (partId: string) => {
    const { data, error } = await supabase
      .from('spare_part_images')
      .select('image_url, image_url_thumb, comment, file_size_bytes, log_entry_id, note_id')
      .eq('spare_part_id', partId)
      .order('display_order');

    if (!error && data) {
      const loadedImages = data.map(img => ({
        url: img.image_url,
        thumbnail: img.image_url_thumb,
        comment: img.comment,
        sizeBytes: img.file_size_bytes,
        log_entry_id: img.log_entry_id,
        note_id: img.note_id
      }));
      setImages(loadedImages);
      setInitialImages(JSON.stringify(loadedImages));
      savedImageUrlsRef.current = new Set(loadedImages.map(img => img.url));
    }
  };

  const loadLinkedRecords = async (partId: string) => {
    try {
      const [expensesRes, notesRes] = await Promise.all([
        supabase
          .from('log_entry_parts')
          .select(`
            log_entry_id,
            quantity,
            price_per_unit,
            comment,
            log_entries (
              id,
              date,
              mileage,
              title,
              expenses (*),
              location:locations (*),
              log_entry_images (id),
              log_entry_parts (
                spare_part_id,
                spare_parts (name)
              )
            )
          `)
          .eq('spare_part_id', partId),
        supabase
          .from('note_spare_parts')
          .select(`
            note_id,
            quantity,
            price_per_unit,
            comment,
            notes (
              id,
              title,
              content,
              created_at,
              updated_at,
              is_pinned,
              is_public
            )
          `)
          .eq('spare_part_id', partId)
      ]);

      if (!expensesRes.error && expensesRes.data) {
        const sortedExpenses = (expensesRes.data || []).sort((a: any, b: any) => 
          new Date(b.log_entries.date).getTime() - new Date(a.log_entries.date).getTime()
        );
        setLinkedExpenses(sortedExpenses);
      }
      if (!notesRes.error && notesRes.data) {
        const sortedNotes = (notesRes.data || []).sort((a: any, b: any) => 
          new Date(b.notes.updated_at || b.notes.created_at).getTime() - new Date(a.notes.updated_at || a.notes.created_at).getTime()
        );
        setLinkedNotes(sortedNotes);
      }
    } catch (err) {
      console.error('Error loading linked records:', err);
    }
  };

  const loadReminder = async (partId: string) => {
    const { data, error } = await supabase
      .from('reminders')
      .select('*')
      .eq('part_id', partId)
      .maybeSingle();

    if (!error && data) {
      const rData: ReminderFormData = {
        name: data.name || '',
        description: data.description || '',
        remind_date: data.remind_date,
        remind_time: data.remind_time,
        extra_reminders: data.extra_reminders,
        repeat_yearly: data.repeat_yearly,
        is_active: data.is_active,
        action_required: data.action_required
      };
      setReminderData(rData);
      setInitialReminderData(JSON.stringify(rData));
      setActualReminderId(data.id);
    } else {
      setReminderData(null);
      setInitialReminderData('null');
      setActualReminderId(null);
    }
  };

  const handleSubmit = async (e?: React.FormEvent): Promise<boolean> => {
    if (e) e.preventDefault();
    if (isReadOnly || !user || !formData.name.trim()) return false;

    if (reminderData && reminderData.is_active) {
      const now = new Date();
      const remindDateTime = new Date(`${reminderData.remind_date}T${reminderData.remind_time || '00:00'}`);
      // Must be at least 1 minute in the future
      if (remindDateTime.getTime() <= now.getTime() + 60000) {
        setAlertConfig({ 
          isOpen: true, 
          title: t.common.error, 
          message: t.reminders.futureTimeError 
        });
        return false;
      }
    }

    setIsSubmitting(true);

    try {
      const data = {
        name: formData.name,
        manufacturer: formData.manufacturer || null,
        part_code: formData.part_code || null,
        link: formData.link || null,
        price: formData.price ? parseFloat(formData.price) : null,
        quantity: parseInt(formData.quantity as any) || 1,
        comment: formData.comment || null,
        car_id: carId,
        user_id: effectiveUserId,
        expense_id: sparePart?.expense_id || null
      };

      let partId = sparePart?.id;

      if (sparePart) {
        const { error } = await supabase
          .from('spare_parts')
          .update(data)
          .eq('id', sparePart.id);

        if (error) throw error;
      } else {
        const { data: newPart, error } = await supabase
          .from('spare_parts')
          .insert(data)
          .select()
          .single();

        if (error) throw error;
        partId = newPart.id;
      }

      // Handle images
      if (partId) {
        // Delete old images
        await supabase
          .from('spare_part_images')
          .delete()
          .eq('spare_part_id', partId);

        // Insert new images
        if (images.length > 0) {
          const { error: imgError } = await supabase
            .from('spare_part_images')
            .insert(
              images.map((img, index) => ({
                spare_part_id: partId,
                image_url: img.url,
                image_url_thumb: (img as any).thumbnail || img.url,
                comment: img.comment || null,
                display_order: index,
                user_id: effectiveUserId,
                file_size_bytes: img.sizeBytes || 0
              }))
            );
          if (imgError) throw imgError;
        }

        // Handle Reminder
        if (reminderData) {
          const rData: any = {
            car_id: carId,
            user_id: effectiveUserId,
            part_id: partId,
            name: reminderData.name || null,
            description: reminderData.description || null,
            remind_date: reminderData.remind_date,
            remind_time: reminderData.remind_time,
            extra_reminders: reminderData.extra_reminders,
            repeat_yearly: reminderData.repeat_yearly,
            is_active: reminderData.is_active
          };

          // Round to 5m on save
          if (rData.remind_time) {
            const [h, m] = rData.remind_time.split(':').map(Number);
            const roundedM = Math.round(m / 5) * 5;
            const finalM = roundedM === 60 ? 55 : roundedM;
            rData.remind_time = `${h.toString().padStart(2, '0')}:${finalM.toString().padStart(2, '0')}`;
          }

          const { data: existing } = await supabase.from('reminders').select('id').eq('part_id', partId).maybeSingle();
          if (existing) {
            rData.is_seen = false;
            rData.action_required = false;
            rData.last_remind_sequence = 0;
            rData.updated_at = new Date().toISOString();
            await supabase.from('reminders').update(rData).eq('id', existing.id);
          } else {
            await supabase.from('reminders').insert(rData);
          }
        } else if (partId) {
          await supabase.from('reminders').delete().eq('part_id', partId);
        }
      }

      setStoredValues({ ...formData });
      setInitialImages(JSON.stringify(images));

      queryClient.invalidateQueries({ queryKey: PART_KEYS.list(carId) });
      queryClient.invalidateQueries({ queryKey: GALLERY_KEYS.car(carId) });
      queryClient.invalidateQueries({ queryKey: REMINDER_KEYS.list(carId) });

      onSaved();
      return true;
    } catch (error) {
      console.error('Error saving spare part:', error);
      setAlertConfig({ isOpen: true, title: t.common.error, message: 'Error saving spare part' });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const performDelete = async () => {
    if (isReadOnly || !sparePart) return;

    setIsSubmitting(true);

    try {
      // Delete images first
      await supabase
        .from('spare_part_images')
        .delete()
        .eq('spare_part_id', sparePart.id);

      const { error } = await supabase
        .from('spare_parts')
        .delete()
        .eq('id', sparePart.id);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: PART_KEYS.list(carId) });
      queryClient.invalidateQueries({ queryKey: GALLERY_KEYS.car(carId) });
      queryClient.invalidateQueries({ queryKey: REMINDER_KEYS.list(carId) });

      onSaved();
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting spare part:', error);
      setAlertConfig({ isOpen: true, title: t.common.error, message: 'Error deleting spare part' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleBack = () => {
    if (hasChanges) {
      setShowConfirmModal(true);
    } else {
      const unsavedUrls = images
        .map(img => img.url)
        .filter(url => url && !savedImageUrlsRef.current.has(url));
      if (unsavedUrls.length > 0) deleteTempFiles(unsavedUrls);
    onClose();
    }
  };

  const handleSimulateReminder = async () => {
    if (!sparePart?.id || isSubmitting) return;
    setIsSubmitting(true);
    try {
      // Find the actual reminder ID for this spare part
      const { data: remData } = await supabase.from('reminders').select('id').eq('part_id', sparePart.id).maybeSingle();
      if (!remData) {
        setAlertConfig({ isOpen: true, title: t.common.error, message: 'Nav iestatīts atgādinājums šai detaļai' });
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL || 'https://mycarcosts.gtreulands.workers.dev'}/reminders/simulate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({ reminder_id: remData.id })
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText);
      }

      setAlertConfig({
        isOpen: true,
        title: t.common.ok,
        message: t.reminders.simulateSuccess
      });
    } catch (err: any) {
      console.error('Error simulating reminder:', err);
      setAlertConfig({
        isOpen: true,
        title: t.common.error,
        message: err.message || t.common.error
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLinkedToAny = linkedExpenses.length > 0 || linkedNotes.length > 0 || !!sparePart?.expense_id;
  const showSaveAction = !isReadOnly && (!sparePart || hasChanges);

  return (
    <div
      className="fixed inset-0 flex flex-col h-full overflow-hidden z-50 bg-gradient-theme"
      ref={containerRef}
    >
      <NavigationBar
        title={sparePart ? (isReadOnly ? t.spareParts.title : t.spareParts.editPart) : t.spareParts.addPart}
        onBack={handleBack}
        fixed={false}
        rightAction={showSaveAction ? {
          icon: isSubmitting ? Loader2 : Save,
          onClick: () => handleSubmit(),
          disabled: isSubmitting,
          className: isSubmitting ? 'animate-spin' : ''
        } : undefined}
      />

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto"
      >
        <form id="spare-part-form" onSubmit={handleSubmit} className="p-4 space-y-4 pb-10 max-w-2xl mx-auto">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 font-primary">
              {t.spareParts.nameLabel}
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.name}
                disabled={isReadOnly}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  setActiveSuggestionField('name');
                }}
                onFocus={() => setActiveSuggestionField('name')}
                required
                className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-primary disabled:bg-gray-50 dark:disabled:bg-slate-900/20 disabled:text-gray-500"
                placeholder={t.spareParts.partNamePlaceholder}
              />
              {activeSuggestionField === 'name' && formData.name.length >= 2 && (
                <div ref={suggestionRef} className="absolute z-50 w-full mt-1 bg-white dark:bg-[#001529] border border-gray-200 dark:border-white/10 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                  {Array.from(new Set(allParts
                    .map(p => p.name)
                    .filter(n => n.toLowerCase().includes(formData.name.toLowerCase()) && n.toLowerCase() !== formData.name.toLowerCase())
                  )).slice(0, 10).map((suggestion, i) => (
                    <button
                      key={i}
                      type="button"
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-white/5 text-sm dark:text-gray-200 transition-colors border-b last:border-0 border-gray-50 dark:border-white/5"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          name: suggestion
                        }));
                        setActiveSuggestionField(null);
                      }}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 font-primary">
                {t.spareParts.manufacturerLabel}
              </label>
              <input
                type="text"
                value={formData.manufacturer}
                disabled={isReadOnly}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-primary disabled:bg-gray-50 dark:disabled:bg-slate-900/20 disabled:text-gray-500"
                placeholder={t.spareParts.manufacturerPlaceholder}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 font-primary">
                {t.spareParts.partCode}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.part_code}
                  disabled={isReadOnly}
                  onChange={(e) => {
                    setFormData({ ...formData, part_code: e.target.value });
                    setActiveSuggestionField('part_code');
                  }}
                  onFocus={() => setActiveSuggestionField('part_code')}
                  className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-primary uppercase disabled:bg-gray-50 dark:disabled:bg-slate-900/20 disabled:text-gray-500"
                  placeholder={t.spareParts.codePlaceholder}
                />
                {activeSuggestionField === 'part_code' && formData.part_code.length >= 2 && (
                  <div ref={suggestionRef} className="absolute z-50 w-full mt-1 bg-white dark:bg-[#001529] border border-gray-200 dark:border-white/10 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                    {Array.from(new Set(allParts
                      .map(p => p.part_code)
                      .filter(c => c && c.toLowerCase().includes(formData.part_code.toLowerCase()) && c.toLowerCase() !== formData.part_code.toLowerCase())
                    )).slice(0, 10).map((suggestion, i) => (
                      <button
                        key={i}
                        type="button"
                        className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-white/5 text-sm dark:text-gray-200 transition-colors border-b last:border-0 border-gray-50 dark:border-white/5"
                        onClick={() => {
                          setFormData(prev => {
                            const matchingPart = allParts.find(p => p.part_code === suggestion);
                            return {
                              ...prev,
                              part_code: suggestion,
                              name: (matchingPart && !prev.name) ? matchingPart.name : prev.name
                            };
                          });
                          setActiveSuggestionField(null);
                        }}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {!isLinkedToAny && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 font-primary">
                  {t.spareParts.quantity}
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.quantity}
                  disabled={isReadOnly}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  placeholder="1"
                  className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-primary disabled:bg-gray-50 dark:disabled:bg-slate-900/20 disabled:text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 font-primary">
                  {t.spareParts.priceLabel.replace('{currency}', effectivePreferences?.currency_symbol || '€')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  disabled={isReadOnly}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-primary disabled:bg-gray-50 dark:disabled:bg-slate-900/20 disabled:text-gray-500"
                  placeholder="0.00"
                />
              </div>
            </div>
          )}


          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 font-primary">
              {t.expenses.detailedDescription}
            </label>
            <RichTextEditor
              content={formData.comment}
              onChange={(value) => setFormData({ ...formData, comment: value })}
              readOnly={isReadOnly}
              placeholder={t.spareParts.additionalInfoPlaceholder}
              minHeight="120px"
              maxHeight="400px"
            />
          </div>

          {(images.length > 0 || !isReadOnly) && (
            <div>
              <div className="flex items-center justify-between mb-2 px-1">
                <span className={theme.typography.sectionTitle}>
                  {t.spareParts.imagesLabel}{images.length > 0 ? ` (${images.length})` : ''}
                </span>
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={() => !isUploading && imageUploadRef.current?.openFileDialog()}
                    disabled={isUploading}
                    className={`rounded-full p-2 text-white transition-colors shadow-sm ${isUploading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-400"
                      }`}
                  >
                    {isUploading ? (
                      <Loader2 size={22} className="animate-spin" />
                    ) : (
                      <Plus size={22} strokeWidth={2.5} />
                    )}
                  </button>
                )}
              </div>
              <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm p-4">
                {images.length === 0 && !isReadOnly && (
                  <p className="text-sm text-gray-400 dark:text-gray-600 italic mb-3">{t.dashboard.noFiles || 'Nav neviens fails pievienots'}</p>
                )}
                <ImageUpload
                  ref={imageUploadRef}
                  images={images}
                  onChange={setImages}
                  folder="spare-parts"
                  maxImages={countLimit || 10}
                  showComments={true}
                  readOnly={isReadOnly}
                  type="part"
                  itemId={sparePart?.id}
                  hideUploadButton={true}
                  onUploadingChange={setIsUploading}
                  carId={carId}
                />
              </div>
            </div>
          )}

          {(linkedExpenses.length > 0 || linkedNotes.length > 0) && (
            <div className="pt-4">
              <div className="mb-2 px-1">
                <span className={theme.typography.sectionTitle}>{t.spareParts.connectedWithRecords}</span>
              </div>
              <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm divide-y divide-gray-100 dark:divide-white/5">
                {linkedExpenses.map((link, idx) => {
                  const recordId = link.log_entry_id || link.log_entries?.id;
                  const recordImages = images.filter(img => img.log_entry_id === recordId);
                  const hasDetails = (link.quantity && link.quantity > 1) || (link.price_per_unit && link.price_per_unit > 0) || link.comment || recordImages.length > 0;
                  const hasPrimaryDetails = (link.quantity && link.quantity > 1) || (link.price_per_unit && link.price_per_unit > 0) || link.comment;

                  return (
                    <div key={`exp-${idx}`}>
                      <LogEntryItem
                        entry={{
                          ...link.log_entries,
                          location: Array.isArray(link.log_entries.location) ? link.log_entries.location[0] : link.log_entries.location,
                          imageCount: (link.log_entries as any).log_entry_images?.length || 0,
                          partCount: (link.log_entries as any).log_entry_parts?.length || 0,
                          log_entry_parts: link.log_entries.log_entry_parts
                        }}
                        slug={slug || ''}
                        effectivePreferences={effectivePreferences}
                        openInNewTab={true}
                        forceShowYear={true}
                      />
                      {hasDetails && (
                        <div className="ml-14 mr-4 mb-3">
                          <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-xl overflow-hidden">
                            {hasDetails && (
                              <div className="flex items-start gap-3 p-3">
                                <div className="w-9 h-9 rounded-full bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <Wrench size={16} className="text-orange-600 dark:text-orange-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-bold text-gray-900 dark:text-white truncate">
                                      {sparePart?.name}
                                    </span>
                                    {sparePart?.part_code && (
                                      <span className="text-[10px] font-black text-gray-400 bg-gray-100 dark:bg-white/5 px-1.5 py-0.5 rounded uppercase tracking-widest flex-shrink-0">
                                        {sparePart.part_code}
                                      </span>
                                    )}
                                  </div>
                                  {(link.quantity || 1) >= 1 && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      ×{link.quantity}
                                    </span>
                                  )}
                                  {link.comment && (
                                    <p className="mt-0.5 text-[12px] italic text-gray-500 dark:text-gray-400 leading-relaxed">
                                      {link.comment}
                                    </p>
                                  )}
                                </div>
                                {hasDetails && (
                                  <div className="text-right flex-shrink-0">
                                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                                      {(link.price_per_unit || 0).toFixed(2)} {effectivePreferences?.currency_symbol || '€'}
                                    </span>
                                    {(link.quantity || 1) >= 1 && (
                                      <span className="block text-[11px] text-gray-400 dark:text-gray-500">
                                        = {((link.price_per_unit || 0) * (link.quantity || 1)).toFixed(2)} {effectivePreferences?.currency_symbol || '€'}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Images for this expense */}
                            {recordImages.length > 0 && (
                              <div className={`px-3 pb-3 ${hasPrimaryDetails ? '-mt-1' : ''} overflow-x-auto scrollbar-hide`}>
                                <div className="flex gap-2 min-w-0">
                                  {recordImages.map((img, imgIdx) => (
                                    <button
                                      key={imgIdx}
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setViewerImages(recordImages.map(i => i.url));
                                        setViewerIndex(imgIdx);
                                      }}
                                      className="relative w-14 h-14 rounded-lg overflow-hidden border border-gray-100 dark:border-white/10 flex-shrink-0 hover:border-blue-500 transition-colors shadow-sm active:scale-95"
                                    >
                                      <img
                                        src={img.thumbnail || img.url}
                                        alt=""
                                        className="w-full h-full object-cover"
                                      />
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}


                {linkedNotes.map((link, idx) => {
                  const recordId = link.note_id || link.notes?.id;
                  const recordImages = images.filter(img => img.note_id === recordId);
                  const hasDetails = (link.quantity && link.quantity > 1) || (link.price_per_unit && link.price_per_unit > 0) || link.comment || recordImages.length > 0;
                  const hasPrimaryDetails = (link.quantity && link.quantity > 1) || (link.price_per_unit && link.price_per_unit > 0) || link.comment;

                  return (
                    <div key={`note-${idx}`}>
                      <NoteItem
                        note={{
                          ...link.notes,
                          updated_at: link.notes.updated_at || link.notes.created_at
                        }}
                        slug={slug || ''}
                        isAdminView={isAdminView}
                        compact={true}
                        carIsPublic={selectedCar?.is_public}
                        openInNewTab={true}
                        forceShowYear={true}
                      />
                      {hasDetails && (
                        <div className="ml-14 mr-4 mb-3">
                          <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-xl overflow-hidden">
                            {hasDetails && (
                              <div className="flex items-start gap-3 p-3">
                                <div className="w-9 h-9 rounded-full bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <Wrench size={16} className="text-orange-600 dark:text-orange-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-bold text-gray-900 dark:text-white truncate">
                                      {sparePart?.name}
                                    </span>
                                    {sparePart?.part_code && (
                                      <span className="text-[10px] font-black text-gray-400 bg-gray-100 dark:bg-white/5 px-1.5 py-0.5 rounded uppercase tracking-widest flex-shrink-0">
                                        {sparePart.part_code}
                                      </span>
                                    )}
                                  </div>
                                  {(link.quantity || 1) >= 1 && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      ×{link.quantity}
                                    </span>
                                  )}
                                  {link.comment && (
                                    <p className="mt-0.5 text-[12px] italic text-gray-500 dark:text-gray-400 leading-relaxed">
                                      {link.comment}
                                    </p>
                                  )}
                                </div>
                                {hasDetails && (
                                  <div className="text-right flex-shrink-0">
                                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                                      {(link.price_per_unit || 0).toFixed(2)} {effectivePreferences?.currency_symbol || '€'}
                                    </span>
                                    {(link.quantity || 1) >= 1 && (
                                      <span className="block text-[11px] text-gray-400 dark:text-gray-500">
                                        = {((link.price_per_unit || 0) * (link.quantity || 1)).toFixed(2)} {effectivePreferences?.currency_symbol || '€'}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Images for this note */}
                            {recordImages.length > 0 && (
                              <div className={`px-3 pb-3 ${hasPrimaryDetails ? '-mt-1' : ''} overflow-x-auto scrollbar-hide`}>
                                <div className="flex gap-2 min-w-0">
                                  {recordImages.map((img, imgIdx) => (
                                    <button
                                      key={imgIdx}
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setViewerImages(recordImages.map(i => i.url));
                                        setViewerIndex(imgIdx);
                                      }}
                                      className="relative w-14 h-14 rounded-lg overflow-hidden border border-gray-100 dark:border-white/10 flex-shrink-0 hover:border-blue-500 transition-colors shadow-sm active:scale-95"
                                    >
                                      <img
                                        src={img.thumbnail || img.url}
                                        alt=""
                                        className="w-full h-full object-cover"
                                      />
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="pt-4">
            <ReminderFormSection
              data={reminderData}
              onChange={setReminderData}
              isReadOnly={isReadOnly}
              isAdminView={isAdminView}
              onSimulate={handleSimulateReminder}
              reminderId={actualReminderId || undefined}
              onMarkSeen={async () => {
                if (!actualReminderId || !reminderData) return;
                setIsSubmitting(true);
                try {
                  const updateData: any = { 
                    is_seen: true, 
                    action_required: false,
                    updated_at: new Date().toISOString()
                  };

                  if (reminderData.repeat_yearly && reminderData.remind_date) {
                    const currentDate = new Date(reminderData.remind_date);
                    currentDate.setFullYear(currentDate.getFullYear() + 1);
                    updateData.remind_date = currentDate.toISOString().split('T')[0];
                    updateData.is_seen = false;
                  }

                  await supabase.from('reminders').update(updateData).eq('id', actualReminderId);
                  await queryClient.invalidateQueries({ queryKey: REMINDER_KEYS.list(carId) });
                  onClose();
                } catch (err) {
                  console.error('Error marking seen:', err);
                } finally {
                  setIsSubmitting(false);
                }
              }}
            />
          </div>

          {!isReadOnly && (
            <div className="pt-4">
              <div className="mb-2 px-1">
                <span className={theme.typography.sectionTitle}>{t.common.additionalOptions}</span>
              </div>
              <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm divide-y divide-gray-100 dark:divide-white/5">
                {sparePart && (
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isLinkedToAny || isSubmitting}
                      className={`w-full text-left p-4 text-base font-medium transition-colors flex items-center justify-between ${isLinkedToAny
                        ? 'text-gray-400 cursor-not-allowed bg-gray-50/50 dark:bg-slate-900/10'
                        : 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10'
                        }`}
                    >
                      {t.spareParts.deletePart}
                    </button>
                    {isLinkedToAny && (
                      <div className="px-4 pb-4 bg-gray-50/50 dark:bg-slate-900/10">
                        <p className="text-[11px] text-gray-400 dark:text-white/40 italic font-medium leading-relaxed">
                          {t.spareParts.cannotDeleteNote}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </form>
        {/* Extra space for keyboard */}
        <div className="h-[40vh]" />
      </div>
      <ConfirmModal
        isOpen={showConfirmModal}
        title={t.common.unsavedChangesTitle}
        message={t.common.unsavedChangesMessage}
        confirmLabel={t.common.save}
        cancelLabel={t.common.dontSave}
        onConfirm={async () => {
          const success = await handleSubmit();
          if (success) {
            setShowConfirmModal(false);
            onClose();
          }
        }}
        onCancel={() => {
          setShowConfirmModal(false);
          // Delete any unsaved temp files before discarding
          const unsavedUrls = images
            .map(img => img.url)
            .filter(url => url && !savedImageUrlsRef.current.has(url));
          if (unsavedUrls.length > 0) deleteTempFiles(unsavedUrls);
          onClose(); // Discard and leave
        }}
        onClose={() => setShowConfirmModal(false)} // Stay
        isDestructive={false}
      />

      <ConfirmModal
        isOpen={showDeleteModal}
        title={t.spareParts.deletePart}
        message={t.spareParts.deleteConfirm}
        confirmLabel={t.common.delete}
        cancelLabel={t.common.cancel}
        onConfirm={performDelete}
        onCancel={() => setShowDeleteModal(false)}
        onClose={() => setShowDeleteModal(false)}
        isDestructive={true}
      />
      <AlertModal
        isOpen={alertConfig.isOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmLabel={t.common.ok}
        onClose={() => setAlertConfig({ isOpen: false, title: '', message: '' })}
      />

      {viewerImages.length > 0 && (
        <ImageViewer
          images={viewerImages}
          initialIndex={viewerIndex}
          onClose={() => setViewerImages([])}
        />
      )}
    </div>
  );
}
